import React from "react"
import merge from "lodash/merge" // TODO: Replace with own function
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as callouts from "activities/components/callouts"
import * as refinance from "activities/components/refinance"
import AccessibilityTable from "activities/components/shared/accessibility-table"

const RefinanceLoan_ = shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {balance, currentMonthly, currentRate, refinanceTerm, refinanceRate} = getState()
    const isDisabled = currentMonthly == 0
    const {data, interestCurrent, interestRefinance, refinanceMonthly} = refinance.validateData(isDisabled, () =>
      refinance.graphDataAuto(balance, currentMonthly, currentRate / 100, refinanceTerm, refinanceRate / 100)
    )

    const accessibilityTableHeadings = [
      {
        name: "Years",
        key: "years"
      },
      {
        name: "Current Loan",
        key: "current"
      },
      {
        name: "Refinanced Loan",
        key: "refinance"
      }
    ]

    const accessibilityTableData = refinance.getYears(data).map((row) => ({
      years: row[0],
      current: util.formatDollarsNoCents(row[1]),
      refinance: util.formatDollarsNoCents(row[2])
    }))

    const accessibilityTableLabel = "Loan Refinance Comparison"

    return (
      <>
        <shared.SummaryCalcContainer>
          <shared.InputsContainer>
            <refinance.Inputs props={getProps()} state={getState()} updateState={updateState} />
          </shared.InputsContainer>
          <shared.ChartContainer>
            <refinance.Graph props={getProps()} graphData={data} isDisabled={isDisabled} />
          </shared.ChartContainer>
          <shared.SummaryContainer>
            <callouts.Callout>
              <callouts.Detail
                label="New Payment"
                result={refinanceMonthly === Infinity ? "$Infinity" : util.formatDollarsNoCents(util.ifNegative(Number(refinanceMonthly)))}
                dataTestId="new-payment"
              />
              <callouts.Detail label="New Interest" result={util.formatDollarsNoCents(util.ifNegative(Number(interestRefinance)))} dataTestId="new-interest" />
              <callouts.Detail label="New Total Loan Cost" result={util.formatDollarsNoCents(util.ifNegative(Number(interestRefinance + balance)))} dataTestId="total-loan-cost" />
              <callouts.Detail
                label="Difference in Cost"
                result={util.formatDollarsNoCents(Number(interestRefinance - interestCurrent))}
                tooltipText="Total difference in loan cost. A negative number means you'll owe less money overall with the refinanced loan."
                dataTestId="difference-in-cost"
              />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </>
    )
  },
  {
    stateFilter: (state) => {
      const {balance, currentMonthly, currentRate, refinanceTerm, refinanceRate} = state
      const isDisabled = currentMonthly == 0
      const _currentRate = currentRate / 100
      const _refinanceRate = refinanceRate / 100
      const {interestCurrent, interestRefinance, refinanceMonthly} = refinance.validateData(isDisabled, () =>
        refinance.graphDataAuto(balance, currentMonthly, _currentRate, refinanceTerm, _refinanceRate)
      )
      return Object.assign(state, {
        balance,
        currentMonthly,
        currentRate: _currentRate,
        refinanceTerm,
        refinanceRate: _refinanceRate,
        refinanceMonthly,
        refinanceInterest: util.ifNegative(Number(interestRefinance)),
        totalCost: util.ifNegative(Number(interestRefinance + balance)),
        differenceInCost: util.formatDollars(Number(interestRefinance - interestCurrent))
      })
    },
    defaultProps: {
      id: "refinance-loan",
      className: "early-payoff",
      title: "Loan Refinance Calculator",
      subtitle: "Compare a refinanced loan against your current loan.",
      loanTerm: {
        min: 1,
        max: 10,
        step: 1
      },
      interestRate: {
        min: 1.5,
        max: 8,
        step: 0.05
      }
    },
    defaultState: {
      balance: 1000,
      currentMonthly: 0,
      currentRate: 4.75,
      refinanceTerm: 5,
      refinanceRate: 4.75
    }
  }
)

export default (props) => (
  <RefinanceLoan_
    {...merge(
      {
        id: "refinance-auto-loan-monthly",
        title: "Auto Loan Refinance (Monthly Payment)",
        subtitle: "See how refinancing can help you reduce your monthly payment and how much the refinanced loan will cost over time.",
        loanTerm: {
          min: 1,
          max: 120,
          step: 1
        },
        interestRate: {
          min: 0,
          max: 20,
          step: 0.01
        },
        initialState: {
          balance: 10000,
          currentMonthly: 300,
          currentRate: 12,
          refinanceTerm: 60,
          refinanceRate: 8
        },
        ariaLabel:
          "This line graph visually compares a refinanced auto loan against your current auto loan. Read below to find the refinance monthly payment, and the refiance remaining interest."
      },
      props
    )}
  />
)
